import React from 'react';
import { CreditCard, CheckCircle, Clock, XCircle } from 'lucide-react';

const Financeiro: React.FC = () => {
  // Mock data
  const financialData = [
    {
      status: 'Recebidas',
      value: 'R$ 0,00',
      liquidValue: 'R$ 0,00 líquido',
      progress: 0,
      clients: 0,
      billings: 0,
      color: 'bg-success-500',
      textColor: 'text-success-600',
      icon: CheckCircle
    },
    {
      status: 'Confirmadas',
      value: 'R$ 200,00',
      liquidValue: 'R$ 195,53 líquido',
      progress: 20,
      clients: 1,
      billings: 1,
      color: 'bg-primary-500',
      textColor: 'text-primary-600',
      icon: CheckCircle
    },
    {
      status: 'Aguardando pagamento',
      value: 'R$ 9.710,00',
      liquidValue: 'R$ 9.494,35 líquido',
      progress: 85,
      clients: 46,
      billings: 46,
      color: 'bg-warning-500',
      textColor: 'text-warning-600',
      icon: Clock
    },
    {
      status: 'Vencidas',
      value: 'R$ 570,00',
      liquidValue: 'R$ 557,19 líquido',
      progress: 15,
      clients: 3,
      billings: 3,
      color: 'bg-danger-500',
      textColor: 'text-danger-600',
      icon: XCircle
    }
  ];

  const recentTransactions = [
    {
      id: 1,
      cliente: 'João Silva',
      valor: 'R$ 150,00',
      data: '2024-01-15',
      status: 'Pago',
      metodo: 'PIX'
    },
    {
      id: 2,
      cliente: 'Maria Santos',
      valor: 'R$ 200,00',
      data: '2024-01-14',
      status: 'Pendente',
      metodo: 'Boleto'
    },
    {
      id: 3,
      cliente: 'Pedro Costa',
      valor: 'R$ 180,00',
      data: '2024-01-13',
      status: 'Vencido',
      metodo: 'Cartão'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Financeiro</h1>
          <p className="text-gray-600">Gerencie pagamentos e cobranças</p>
        </div>
        <div className="flex items-center gap-x-4">
          <button className="px-4 py-2 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors">Relatórios</button>
          <button className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors">Nova Cobrança</button>
        </div>
      </div>

      {/* Financial Status Cards */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Situação das cobranças
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {financialData.map((item, index) => (
            <div key={index} className="card p-6 relative">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-x-3">
                  <div className={`p-2 rounded-lg ${item.color.replace('bg-', 'bg-').replace('-500', '-100')}`}>
                    <item.icon className={`h-5 w-5 ${item.textColor}`} />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">{item.status}</p>
                    <p className={`text-2xl font-bold ${item.textColor}`}>{item.value}</p>
                    <p className="text-sm text-gray-500">{item.liquidValue}</p>
                  </div>
                </div>
              </div>
              
              {/* Progress bar */}
              <div className="mb-4">
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full ${item.color}`}
                    style={{ width: `${item.progress}%` }}
                  ></div>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-500">
                  {item.clients} cliente{item.clients !== 1 ? 's' : ''}, {item.billings} cobrança{item.billings !== 1 ? 's' : ''}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Transactions */}
        <div className="lg:col-span-2">
          <div className="card p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Transações Recentes
            </h3>
            
            <div className="space-y-4">
              {recentTransactions.map((transaction) => (
                <div key={transaction.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50">
                  <div className="flex items-center gap-x-4">
                    <div className="p-2 bg-primary-100 rounded-lg">
                      <CreditCard className="h-5 w-5 text-primary-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900">{transaction.cliente}</h4>
                      <p className="text-sm text-gray-500">{transaction.metodo} • {new Date(transaction.data).toLocaleDateString('pt-BR')}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-gray-900">{transaction.valor}</p>
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                      transaction.status === 'Pago' 
                        ? 'bg-green-100 text-green-800' 
                        : transaction.status === 'Pendente'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {transaction.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Financial Summary */}
        <div className="space-y-6">
          <div className="card p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Resumo Financeiro
            </h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Receita Total</span>
                <span className="font-semibold text-green-600">R$ 10.480,00</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Recebido</span>
                <span className="font-semibold text-green-600">R$ 200,00</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Pendente</span>
                <span className="font-semibold text-yellow-600">R$ 9.710,00</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Vencido</span>
                <span className="font-semibold text-red-600">R$ 570,00</span>
              </div>
            </div>
          </div>

          <div className="card p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Métodos de Pagamento
            </h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-x-2">
                  <div className="w-3 h-3 bg-primary-500 rounded-full"></div>
                  <span className="text-sm text-gray-600">PIX</span>
                </div>
                <span className="text-sm font-medium">45%</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-x-2">
                  <div className="w-3 h-3 bg-success-500 rounded-full"></div>
                  <span className="text-sm text-gray-600">Cartão</span>
                </div>
                <span className="text-sm font-medium">35%</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-x-2">
                  <div className="w-3 h-3 bg-warning-500 rounded-full"></div>
                  <span className="text-sm text-gray-600">Boleto</span>
                </div>
                <span className="text-sm font-medium">20%</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Financeiro;
